import { api } from '@/lib/axios';
import { renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';

/* ----------------------------- Comptabilité ------------------------------ */
export type CompteType = 'BILAN' | 'RESULTAT';
export type SensNormal = 'DEBIT' | 'CREDIT';
export type JournalType = 'ACHATS' | 'VENTES' | 'BANQUE' | 'CAISSE' | 'OPERATIONS_DIVERSES';
export type ExerciceStatut = 'OUVERT' | 'CLOTURE';
export type EcritureStatut = 'BROUILLON' | 'VALIDE' | 'VERROUILLE';

export interface CompteSCF {
  numero: string;
  libelle_fr: string;
  libelle_ar?: string | null;
  classe: number;
  type_compte: CompteType;
  sens_normal: SensNormal;
  compte_parent?: string | null;
  is_active: boolean;
}

export interface CompteInput {
  numero: string;
  libelle_fr: string;
  libelle_ar?: string | null;
  classe: number;
  type_compte?: CompteType | null;
  sens_normal?: SensNormal | null;
  compte_parent?: string | null;
}

export interface CompteImportResult {
  crees: number;
  mis_a_jour: number;
  ignores: number;
  erreurs: string[];
}

export interface Journal {
  id: string;
  code: string;
  libelle: string;
  type: JournalType;
  compte_contrepartie?: string | null;
}

export interface Exercice {
  id: string;
  annee: number;
  date_debut?: string | null;
  date_fin?: string | null;
  statut: ExerciceStatut;
}

export interface LigneEcritureInput {
  compte_numero: string;
  libelle?: string | null;
  montant_debit: number;
  montant_credit: number;
  tiers_id?: string | null;
}

export interface EcritureInput {
  journal_id: string;
  exercice_id?: string | null;
  date_ecriture: string;
  numero_piece?: string | null;
  reference_doc?: string | null;
  libelle: string;
  chantier_id?: string | null;
  lignes: LigneEcritureInput[];
}

export interface LigneEcritureDTO extends LigneEcritureInput {
  id: string;
}

export interface EcritureDTO {
  id: string;
  numero: number | null;
  journal_id: string | null;
  exercice_id: string | null;
  date_ecriture: string;
  numero_piece?: string | null;
  reference_doc?: string | null;
  libelle: string;
  chantier_id?: string | null;
  statut: EcritureStatut;
  lignes: LigneEcritureDTO[];
}

export interface LigneBalance {
  compte_numero: string;
  libelle: string;
  total_debit: number;
  total_credit: number;
  solde_debiteur: number;
  solde_crediteur: number;
}

export interface Balance {
  lignes: LigneBalance[];
  total_debit: number;
  total_credit: number;
}

export async function listComptes(opts?: { q?: string; classe?: number; inclure_inactifs?: boolean }): Promise<CompteSCF[]> {
  const { data } = await api.get<CompteSCF[]>('/comptabilite/comptes', {
    params: { q: opts?.q, classe: opts?.classe, inclure_inactifs: opts?.inclure_inactifs },
  });
  return data;
}

export async function createCompte(payload: CompteInput): Promise<CompteSCF> {
  const { data } = await api.post<CompteSCF>('/comptabilite/comptes', payload);
  return data;
}

export async function updateCompte(numero: string, payload: Partial<CompteInput> & { is_active?: boolean }): Promise<CompteSCF> {
  const { data } = await api.put<CompteSCF>(`/comptabilite/comptes/${numero}`, payload);
  return data;
}

export async function importComptes(file: File): Promise<CompteImportResult> {
  const form = new FormData();
  form.append('fichier', file);
  const { data } = await api.post<CompteImportResult>('/comptabilite/comptes/import', form);
  return data;
}

export async function listJournaux(): Promise<Journal[]> {
  const { data } = await api.get<Journal[]>('/comptabilite/journaux');
  return data;
}

export async function createJournal(payload: Omit<Journal, 'id'>): Promise<Journal> {
  const { data } = await api.post<Journal>('/comptabilite/journaux', payload);
  return data;
}

export async function updateJournal(id: string, payload: Partial<Omit<Journal, 'id'>>): Promise<Journal> {
  const { data } = await api.put<Journal>(`/comptabilite/journaux/${id}`, payload);
  return data;
}

export async function deleteJournal(id: string): Promise<void> {
  await api.delete(`/comptabilite/journaux/${id}`);
}

export async function listExercices(): Promise<Exercice[]> {
  const { data } = await api.get<Exercice[]>('/comptabilite/exercices');
  return data;
}

export async function createExercice(payload: { annee: number; date_debut?: string | null; date_fin?: string | null }): Promise<Exercice> {
  const { data } = await api.post<Exercice>('/comptabilite/exercices', payload);
  return data;
}

export async function updateExercice(id: string, payload: { statut?: ExerciceStatut; date_debut?: string | null; date_fin?: string | null }): Promise<Exercice> {
  const { data } = await api.put<Exercice>(`/comptabilite/exercices/${id}`, payload);
  return data;
}

export async function deleteExercice(id: string): Promise<void> {
  await api.delete(`/comptabilite/exercices/${id}`);
}

export interface EcritureFiltres {
  exercice_id?: string;
  journal_id?: string;
  statut?: EcritureStatut;
  date_debut?: string;
  date_fin?: string;
  compte_numero?: string;
  page?: number;
  size?: number;
}

export async function createEcriture(payload: EcritureInput): Promise<EcritureDTO> {
  const { data } = await api.post<EcritureDTO>('/comptabilite/ecritures', payload);
  return data;
}

export async function listEcritures(filtres: EcritureFiltres = {}): Promise<Page<EcritureDTO>> {
  const { data } = await api.get<Page<EcritureDTO>>('/comptabilite/ecritures', { params: { size: 100, ...filtres } });
  return data;
}

export async function updateEcriture(id: string, payload: EcritureInput): Promise<EcritureDTO> {
  const { data } = await api.put<EcritureDTO>(`/comptabilite/ecritures/${id}`, payload);
  return data;
}

export async function deleteEcriture(id: string): Promise<void> {
  await api.delete(`/comptabilite/ecritures/${id}`);
}

export async function validerEcriture(id: string): Promise<EcritureDTO> {
  const { data } = await api.post<EcritureDTO>(`/comptabilite/ecritures/${id}/valider`);
  return data;
}

export async function contrepasserEcriture(id: string, date_ecriture?: string): Promise<EcritureDTO> {
  const { data } = await api.post<EcritureDTO>(`/comptabilite/ecritures/${id}/contrepasser`, { date_ecriture: date_ecriture ?? null });
  return data;
}

export async function getBalance(): Promise<Balance> {
  const { data } = await api.get<Balance>('/comptabilite/balance');
  return data;
}

/* ----------------------------- Passerelle comptable ---------------------- */
export type PasserelleType = 'ventes' | 'achats' | 'paie';

/** Résultat d'une génération de période (idempotent). Champs présents selon le type. */
export interface PasserelleGeneration {
  plan_certifie: boolean;
  problemes?: string[];
  // ventes
  factures_creees?: number;
  factures_deja?: number;
  a_affecter?: number;
  factures_ignorees?: number;
  encaissements_crees?: number;
  encaissements_deja?: number;
  // achats (en plus)
  immobilisations?: number;
  reglements_crees?: number;
  reglements_deja?: number;
  // paie
  ecriture_creee?: boolean;
  deja?: boolean;
  bulletins?: number;
  lignes_a_affecter?: number;
}

/** Réconciliation d'une période. Les montants arrivent en STRING (Decimal sérialisé). */
export interface PasserelleReconciliation {
  mois: number;
  annee: number;
  ht_factures?: string;
  produit_mouvements?: string; // ventes
  charge_mouvements?: string; // achats
  ecart_ht?: string;
  tva_basculee_4457?: string; // ventes
  tva_collectee_g50?: string; // ventes
  tva_basculee_4456?: string; // achats
  tva_deductible_g50?: string; // achats
  ecart_tva?: string;
  solde_44587_periode?: string; // ventes (delta mensuel — informatif)
  solde_44586_periode?: string; // achats (delta mensuel — informatif)
  solde_44587_cumule?: string; // ventes (running balance — métrique de vérité, ≥ 0)
  solde_44586_cumule?: string; // achats (running balance — métrique de vérité, ≥ 0)
  ecritures_a_affecter?: { id: string; reference: string | null }[];
  // paie (pas de TVA)
  ecart_brut?: string;
  ecart_net?: string;
  ecart_cotisations?: string;
  ecart_irg?: string;
  ecart_patronale?: string;
  controle_identite?: string;
}

/** Génère les écritures BROUILLON de la période (idempotent : aucun doublon si regénéré). */
export async function genererPasserelle(type: PasserelleType, mois: number, annee: number): Promise<PasserelleGeneration> {
  const { data } = await api.post<PasserelleGeneration>(`/comptabilite/passerelle/${type}/generer`, null, { params: { mois, annee } });
  return data;
}

/** Réconciliation d'une période (lecture seule). */
export async function getPasserelleReconciliation(type: PasserelleType, mois: number, annee: number): Promise<PasserelleReconciliation> {
  const { data } = await api.get<PasserelleReconciliation>(`/comptabilite/passerelle/${type}/reconciliation`, { params: { mois, annee } });
  return data;
}

/** Brouillons d'écritures de la période (réutilise la liste d'écritures filtrée par statut + dates). */
export async function listBrouillonsPeriode(mois: number, annee: number): Promise<EcritureDTO[]> {
  const mm = String(mois).padStart(2, '0');
  const dernierJour = new Date(annee, mois, 0).getDate();
  const page = await listEcritures({
    statut: 'BROUILLON',
    date_debut: `${annee}-${mm}-01`,
    date_fin: `${annee}-${mm}-${String(dernierJour).padStart(2, '0')}`,
    size: 100,
  });
  return page.items;
}

/** Certification du plan pour un type de passerelle (comptes requis présents / actifs / feuilles). */
export interface PasserelleCertif { plan_certifie: boolean; problemes: string[] }
export async function getPasserelleCertifier(type: PasserelleType): Promise<PasserelleCertif> {
  const { data } = await api.get<PasserelleCertif>(`/comptabilite/passerelle/${type}/certifier`);
  return data;
}

/** Règle d'imputation « nature/catégorie/rôle → compte » (clé normalisée pour un affichage unifié). */
export interface PasserelleRegle { cle: string; libelle: string; compte_numero: string; source: string }
const REGLE_KEY: Record<PasserelleType, 'nature' | 'categorie' | 'role'> = {
  ventes: 'nature', achats: 'categorie', paie: 'role',
};
export async function listPasserelleRegles(type: PasserelleType): Promise<PasserelleRegle[]> {
  const { data } = await api.get<Record<string, string>[]>(`/comptabilite/passerelle/${type}/regles`);
  const key = REGLE_KEY[type];
  return data.map((r) => ({ cle: r[key], libelle: r.libelle, compte_numero: r.compte_numero, source: r.source }));
}
export async function updatePasserelleRegle(type: PasserelleType, cle: string, compte_numero: string): Promise<PasserelleRegle> {
  const key = REGLE_KEY[type];
  const { data } = await api.put<Record<string, string>>(`/comptabilite/passerelle/${type}/regles`, { [key]: cle, compte_numero });
  return { cle: data[key], libelle: data.libelle, compte_numero: data.compte_numero, source: data.source };
}

/* ----------------------------- Clôture mensuelle ------------------------- */
export interface MoisCloture {
  annee: number;
  mois: number;
  statut: 'OUVERT' | 'CLOTURE';
  cloture_le: string | null;
  cloture_par: string | null;
  reouvert_le: string | null;
  reouvert_par: string | null;
  motif_reouverture: string | null;
}
/** Les 12 mois de l'année avec statut + traçabilité (clôture / réouverture). */
export async function listClotureMensuelle(annee: number): Promise<MoisCloture[]> {
  const { data } = await api.get<MoisCloture[]>('/comptabilite/cloture-mensuelle', { params: { annee } });
  return data;
}
/** Clôture un mois (DFC/COMPTABLE/direction). */
export async function cloturerMois(mois: number, annee: number): Promise<void> {
  await api.post('/comptabilite/cloture-mensuelle/cloturer', null, { params: { mois, annee } });
}
/** Rouvre un mois clôturé (ADMIN, motif obligatoire — tracé). */
export async function rouvrirMois(mois: number, annee: number, motif: string): Promise<void> {
  await api.post('/comptabilite/cloture-mensuelle/rouvrir', { motif }, { params: { mois, annee } });
}

/* ------------------------------- Fiscalité G50 --------------------------- */
export type G50Statut = 'CALCULEE' | 'DEPOSEE' | 'PAYEE';
export interface G50DTO {
  id: string;
  mois: number;
  annee: number;
  tva_collectee: number;
  tva_deductible: number;
  tva_nette: number;
  tap: number;
  irg_salaires: number;
  acomptes_ibs: number;
  total_a_payer: number;
  statut: G50Statut;
}

export interface G50LigneTaux { taux: number; tva: number }
export interface G50Detail {
  mois: number; annee: number; statut: G50Statut;
  tva_collectee: number; tva_deductible: number; solde_tva: number;
  collectee_par_taux: G50LigneTaux[]; deductible_par_taux: G50LigneTaux[];
  precompte_anterieur: number; precompte_anterieur_amorcage: boolean; precompte_anterieur_modifiable: boolean;
  amorcage_mois: number | null; amorcage_annee: number | null;
  precompte_reporte: number; tva_a_payer: number;
  tap: number; acomptes_ibs: number; irg_salaires: number; droit_timbre: number; total_a_payer: number;
  devalide_le: string | null; devalide_par: string | null;
}
export interface AmorcagePayload { precompte_amorcage: number; mois: number | null; annee: number | null }
export interface G50Manual { tap: number; acomptes_ibs: number; irg_salaires: number; droit_timbre: number }
export interface EtatFournisseurLigne {
  tiers_nom: string | null; tiers_nif: string | null; tiers_nis: string | null; tiers_rc: string | null; tiers_adresse: string | null;
  numero_facture: string | null; date_facture: string | null; date_reglement: string | null;
  montant_tranche: number; tva_tranche: number;
}
export interface G50TvaLigne {
  facture_id: string; sens: 'ACHAT' | 'VENTE';
  tiers_nom: string | null; tiers_nif: string | null;
  numero_facture: string | null; date_facture: string | null; date_tranche: string | null;
  periode_mois: number; periode_annee: number;
  montant_tranche: number; ht_tranche: number; tva_tranche: number;
}

export async function getTvaDeductibleDetail(mois: number, annee: number): Promise<G50TvaLigne[]> {
  const { data } = await api.get<G50TvaLigne[]>('/fiscalite/g50/tva-deductible', { params: { mois, annee } });
  return data;
}
export async function getTvaCollecteeDetail(mois: number, annee: number): Promise<G50TvaLigne[]> {
  const { data } = await api.get<G50TvaLigne[]>('/fiscalite/g50/tva-collectee', { params: { mois, annee } });
  return data;
}

export async function getG50Detail(mois: number, annee: number): Promise<G50Detail> {
  const { data } = await api.get<G50Detail>('/fiscalite/g50/detail', { params: { mois, annee } });
  return data;
}
export async function saveG50Manual(mois: number, annee: number, payload: G50Manual): Promise<G50Detail> {
  const { data } = await api.put<G50Detail>('/fiscalite/g50/detail', payload, { params: { mois, annee } });
  return data;
}
export async function validerG50(mois: number, annee: number): Promise<G50Detail> {
  const { data } = await api.post<G50Detail>('/fiscalite/g50/valider', null, { params: { mois, annee } });
  return data;
}
export async function devaliderG50(mois: number, annee: number): Promise<G50Detail> {
  const { data } = await api.post<G50Detail>('/fiscalite/g50/devalider', null, { params: { mois, annee } });
  return data;
}
export async function getAmorcage(): Promise<AmorcagePayload> {
  const { data } = await api.get<AmorcagePayload>('/fiscalite/amorcage');
  return data;
}
export async function setAmorcage(precompte_amorcage: number, mois: number | null, annee: number | null): Promise<AmorcagePayload> {
  const { data } = await api.put<AmorcagePayload>('/fiscalite/amorcage', { precompte_amorcage, mois, annee });
  return data;
}
export async function getEtatFournisseurs(mois: number, annee: number): Promise<EtatFournisseurLigne[]> {
  const { data } = await api.get<EtatFournisseurLigne[]>('/fiscalite/g50/etat-fournisseurs', { params: { mois, annee } });
  return data;
}
export async function openG50Pdf(mois: number, annee: number): Promise<void> {
  await renderPdf('/fiscalite/g50/pdf', { mois, annee }, 'open');
}
export async function openEtatFournisseursPdf(mois: number, annee: number): Promise<void> {
  await renderPdf('/fiscalite/g50/etat-fournisseurs/pdf', { mois, annee }, 'open');
}
export async function downloadG50Csv(mois: number, annee: number): Promise<void> {
  const { data } = await api.get('/fiscalite/g50/export.csv', { params: { mois, annee }, responseType: 'blob' });
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement('a');
  a.href = url; a.download = `G50-fournisseurs-${annee}-${String(mois).padStart(2, '0')}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
export interface GabaritStatut { present: boolean; taille?: number; modifie?: string }
export async function getGabaritStatut(): Promise<GabaritStatut> {
  const { data } = await api.get<GabaritStatut>('/fiscalite/g50/gabarit');
  return data;
}
export async function uploadGabarit(file: File): Promise<{ present: boolean; nom?: string; feuilles_manquantes?: string[] }> {
  const fd = new FormData();
  fd.append('fichier', file);
  const { data } = await api.post('/fiscalite/g50/gabarit', fd);
  return data;
}
export async function downloadG50Xlsx(mois: number, annee: number): Promise<void> {
  let blob: Blob;
  try {
    const res = await api.get('/fiscalite/g50/export.xlsx', { params: { mois, annee }, responseType: 'blob' });
    blob = res.data as Blob;
  } catch (e: unknown) {
    const data = (e as { response?: { data?: unknown } }).response?.data;
    if (data instanceof Blob) {
      let detail = 'Export G50 impossible.';
      try { detail = JSON.parse(await data.text()).detail ?? detail; } catch { /* ignore */ }
      throw new Error(detail);
    }
    throw e;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `G50-${annee}-${String(mois).padStart(2, '0')}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
export async function listG50(annee?: number): Promise<G50DTO[]> {
  const { data } = await api.get<G50DTO[]>('/fiscalite/g50', { params: { annee } });
  return data;
}

/* -------------------------------- Trésorerie ----------------------------- */
export type TypeCompteTresorerie = 'BANQUE' | 'CCP' | 'CAISSE';
export interface CompteTresorerieDTO {
  id: string;
  libelle: string;
  type: TypeCompteTresorerie;
  banque: string | null;
  solde_actuel: number;
}

export interface TresorerieDashboardDTO {
  solde_total: number;
  par_compte: Array<{ libelle: string; type: string; solde: number }>;
  entrees_mois: number;
  sorties_mois: number;
}

export async function listComptesTresorerie(): Promise<CompteTresorerieDTO[]> {
  const { data } = await api.get<CompteTresorerieDTO[]>('/tresorerie/comptes');
  return data;
}

export async function createCompteTresorerie(payload: { libelle: string; type: TypeCompteTresorerie; banque?: string; solde_initial: number }): Promise<CompteTresorerieDTO> {
  const { data } = await api.post<CompteTresorerieDTO>('/tresorerie/comptes', payload);
  return data;
}

export async function createMouvement(payload: { compte_id: string; date_mouvement: string; sens: 'ENTREE' | 'SORTIE'; montant: number; libelle: string }): Promise<unknown> {
  const { data } = await api.post('/tresorerie/mouvements', payload);
  return data;
}

export async function getTresorerieDashboard(): Promise<TresorerieDashboardDTO> {
  const { data } = await api.get<TresorerieDashboardDTO>('/tresorerie/dashboard');
  return data;
}

/* ------------------------- Caisses d'avance (régie) ----------------------- */
export interface Caisse {
  id: string;
  libelle: string;
  detenteur_id: string | null;
  detenteur_nom: string | null;
  solde_actuel: number;
}
export interface CaisseMouvement {
  id: string;
  date_mouvement: string;
  sens: 'ENTREE' | 'SORTIE';
  montant: number;
  libelle: string;
  categorie: string | null;
  chantier_id: string | null;
  justificatif_url: string | null;
  comptabilise: boolean;
  source_type: string | null;
}

export async function listCaisses(): Promise<Caisse[]> {
  const { data } = await api.get<Caisse[]>('/tresorerie/caisses');
  return data;
}
export async function createCaisse(payload: { libelle: string; detenteur_id: string | null; solde_initial?: number }): Promise<Caisse> {
  const { data } = await api.post<Caisse>('/tresorerie/caisses', payload);
  return data;
}
export async function getCaisseMouvements(caisseId: string): Promise<CaisseMouvement[]> {
  const { data } = await api.get<CaisseMouvement[]>(`/tresorerie/caisses/${caisseId}/mouvements`);
  return data;
}
export async function alimenterCaisse(payload: { source_compte_id: string; caisse_id: string; montant: number; date_mouvement: string; libelle?: string | null }): Promise<unknown> {
  const { data } = await api.post('/tresorerie/transfert', payload);
  return data;
}
export async function depenseCaisse(caisseId: string, payload: { montant: number; libelle: string; date_mouvement: string; categorie?: string | null; chantier_id?: string | null; justificatif_url?: string | null }): Promise<unknown> {
  const { data } = await api.post(`/tresorerie/caisses/${caisseId}/depense`, payload);
  return data;
}
export async function comptabiliserMouvement(mouvementId: string): Promise<unknown> {
  const { data } = await api.post(`/tresorerie/mouvements/${mouvementId}/comptabiliser`);
  return data;
}

/* ------------------------------ Sous-traitants --------------------------- */
export interface ContratSTDTO {
  id: string;
  reference: string;
  sous_traitant_id: string;
  chantier_id: string | null;
  objet: string;
  montant_contrat: number;
  retenue_garantie_pct: number;
  avancement_pct: number;
  montant_valide: number;
  montant_paye: number;
  retenue_constituee: number;
  statut: string;
  is_archived: boolean;
}

export interface SousTraitantsDashboardDTO {
  exposition_totale: number;
  reglements_en_attente: number;
  retenues_constituees: number;
  nb_contrats_actifs: number;
}

export async function listContratsST(params?: { archives?: boolean }): Promise<Page<ContratSTDTO>> {
  const { data } = await api.get<Page<ContratSTDTO>>('/sous-traitants', { params });
  return data;
}

export async function updateContratST(id: string, payload: { is_archived?: boolean; statut?: string }): Promise<ContratSTDTO> {
  const { data } = await api.put<ContratSTDTO>(`/sous-traitants/${id}`, payload);
  return data;
}

export async function deleteContratST(id: string): Promise<void> {
  await api.delete(`/sous-traitants/${id}`);
}

export async function getSousTraitantsDashboard(): Promise<SousTraitantsDashboardDTO> {
  const { data } = await api.get<SousTraitantsDashboardDTO>('/sous-traitants/dashboard');
  return data;
}

/* ---------------------- Attachements de sous-traitant --------------------- */
export type StatutAttachementST = 'BROUILLON' | 'VALIDE' | 'PAYE';

export interface AttachementSTLigneInput {
  designation: string;
  unite?: string | null;
  quantite_periode: number;
  quantite_cumul: number;
  prix_unitaire: number;
  ordre?: number;
}
export interface AttachementSTLigneDTO extends AttachementSTLigneInput {
  id: string;
  montant: number;
}
export interface AttachementSTDTO {
  id: string;
  reference: string | null;
  statut: StatutAttachementST;
  sous_traitant_id: string;
  sous_traitant_nom: string | null;
  chantier_id: string;
  chantier_label: string | null;
  contrat_id: string | null;
  contrat_reference: string | null;
  date_attachement: string | null;
  periode_du: string | null;
  periode_au: string | null;
  objet: string | null;
  etabli_par_id: string | null;
  etabli_par_nom: string | null;
  retenue_garantie_pct: number;
  montant_brut: number;
  montant_rg: number;
  montant_net: number;
  facture_id: string | null;
  facture_reference: string | null;
  observations: string | null;
  is_archived: boolean;
  lignes: AttachementSTLigneDTO[];
  // Plafond contractuel (avenants inclus dans montant_contrat).
  contrat_montant: number;
  cumul_attache_autres: number;
  reste_disponible: number;
  depasse_plafond: boolean;
}
export interface AttachementSTInput {
  sous_traitant_id: string;
  chantier_id: string;
  contrat_id?: string | null;
  date_attachement?: string | null;
  periode_du?: string | null;
  periode_au?: string | null;
  objet?: string | null;
  etabli_par_id?: string | null;
  retenue_garantie_pct?: number | null;
  observations?: string | null;
  lignes: AttachementSTLigneInput[];
}

export async function listAttachementsST(params?: { contrat_id?: string; sous_traitant_id?: string; statut?: string; archives?: boolean }): Promise<AttachementSTDTO[]> {
  const { data } = await api.get<AttachementSTDTO[]>('/sous-traitants/attachements', { params });
  return data;
}
export async function createAttachementST(payload: AttachementSTInput): Promise<AttachementSTDTO> {
  const { data } = await api.post<AttachementSTDTO>('/sous-traitants/attachements', payload);
  return data;
}
export async function updateAttachementST(id: string, payload: Partial<AttachementSTInput> & { is_archived?: boolean }): Promise<AttachementSTDTO> {
  const { data } = await api.put<AttachementSTDTO>(`/sous-traitants/attachements/${id}`, payload);
  return data;
}
export async function validerAttachementST(id: string): Promise<AttachementSTDTO> {
  const { data } = await api.post<AttachementSTDTO>(`/sous-traitants/attachements/${id}/valider`);
  return data;
}
export async function deleteAttachementST(id: string): Promise<void> {
  await api.delete(`/sous-traitants/attachements/${id}`);
}

/* --------------------------- États financiers ---------------------------- */
export interface LigneEtat {
  numero: string;
  libelle: string;
  classe: number;
  montant: number;
}
export interface PalierTCR { code: string; libelle: string; montant: number }
export interface CompteResultatDTO {
  produits: LigneEtat[];
  charges: LigneEtat[];
  total_produits: number;
  total_charges: number;
  cascade: PalierTCR[];
  resultat: number;
  resultat_bilan: number;
  coherent: boolean;
}
export interface RubriqueActifSCF {
  libelle: string;
  lignes: { numero: string; libelle: string; brut: number }[];
  brut: number; amortissement: number; net: number;
}
export interface RubriquePassifSCF {
  libelle: string;
  lignes: { numero: string; libelle: string; montant: number }[];
  total: number;
}
export interface BilanSCFDTO {
  actif: RubriqueActifSCF[];
  passif: RubriquePassifSCF[];
  total_actif_net: number;
  total_passif: number;
  resultat: number;
  equilibre: boolean;
  ecart: number;
  comptes_non_classes?: { numero: string; libelle: string; montant: number }[];
}
export interface LiasseDTO {
  bilan: BilanSCFDTO;
  tcr: { paliers: PalierTCR[]; resultat_net: number; resultat_bilan: number; coherent: boolean };
  tableau9: { benefice_comptable: number; reintegrations: unknown[]; deductions: unknown[]; resultat_fiscal: number };
  annexes: { numero: number; titre: string; statut: string }[];
}

export async function getCompteResultat(): Promise<CompteResultatDTO> {
  const { data } = await api.get<CompteResultatDTO>('/comptabilite/compte-resultat');
  return data;
}
export async function getBilan(): Promise<BilanSCFDTO> {
  const { data } = await api.get<BilanSCFDTO>('/comptabilite/bilan');
  return data;
}
export async function getLiasse(): Promise<LiasseDTO> {
  const { data } = await api.get<LiasseDTO>('/comptabilite/liasse');
  return data;
}
/** Télécharge/ouvre la liasse (PDF dans un onglet, Excel en téléchargement). */
export async function downloadLiasse(format: 'pdf' | 'xlsx'): Promise<void> {
  const mime = format === 'pdf'
    ? 'application/pdf'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const { data } = await api.get(`/comptabilite/liasse/export.${format}`, { responseType: 'blob' });
  // Téléchargement via <a download> (fiable, jamais bloqué par l'anti-popup contrairement à
  // window.open appelé après un await). L'utilisateur ouvre le PDF téléchargé pour l'imprimer.
  const blob = new Blob([data as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `liasse_fiscale.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}
